import { useLazyQuery, useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import { Alert, Button, Paper, Skeleton, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Permission, WasteInvoiceDetailQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ManifestDetails as ManifestDetailsV2 } from "../Manifests/ManifestDetails";
import { ManifestDetails as ManifestDetailsV1 } from "../Manifests/ManifestDetailsV1";
import { InvoiceLinesTable } from "./Detail/InvoiceLinesTable";
import { DetailFact, DetailFactsCard, DetailSection } from "./DetailLayout";
import { InvoiceTrackingDetailPage } from "./InvoiceTrackingDetailPage";
import { useInvoiceTrackingLink } from "./invoiceTrackingPeriod";
import { GET_WASTE_INVOICE_DOCUMENT_DOWNLOAD_URL, WASTE_INVOICE_DETAIL_QUERY } from "./queries";
import { formatInvoiceDate, formatInvoiceMoney, formatServicePeriod } from "./utils";

type InvoiceDetail = NonNullable<WasteInvoiceDetailQuery["wasteInvoice"]>;

function InvoiceContent({
  invoice,
  tenantId,
  onManifestClick,
}: {
  invoice: InvoiceDetail;
  tenantId: string;
  onManifestClick: (manifestId: string) => void;
}) {
  return (
    <>
      <DetailFactsCard title={`Invoice ${invoice.invoiceNumber}`}>
        <DetailFact label="Vendor">{invoice.vendor.name}</DetailFact>
        <DetailFact label="Invoice date">{formatInvoiceDate(invoice.invoiceDate)}</DetailFact>
        <DetailFact label="Service period">
          {formatServicePeriod(invoice.servicePeriodStart, invoice.servicePeriodEnd)}
        </DetailFact>
        <DetailFact label="Invoice total">{formatInvoiceMoney(invoice.invoiceTotal)}</DetailFact>
      </DetailFactsCard>
      <DetailSection title="Invoice lines">
        <InvoiceLinesTable
          key={invoice.id}
          invoiceId={invoice.id}
          tenantId={tenantId}
          onManifestClick={onManifestClick}
        />
      </DetailSection>
    </>
  );
}

function InvoiceDetailBody({
  invoice,
  loading,
  error,
  tenantId,
  onManifestClick,
}: {
  invoice: InvoiceDetail | null | undefined;
  loading: boolean;
  error: Error | undefined;
  tenantId: string | undefined;
  onManifestClick: (manifestId: string) => void;
}) {
  if (loading && !invoice) {
    return (
      <>
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={420} />
      </>
    );
  }
  if (error) return <Alert severity="error">{error.message}</Alert>;
  if (!invoice || !tenantId) {
    return (
      <Paper elevation={0} sx={{ p: 6, borderRadius: 2, textAlign: "center" }}>
        <Typography variant="h6">Invoice not found</Typography>
      </Paper>
    );
  }
  return <InvoiceContent invoice={invoice} tenantId={tenantId} onManifestClick={onManifestClick} />;
}

export function WasteInvoiceDetail() {
  const { wasteInvoiceId } = useParams<{ wasteInvoiceId: string }>();
  const { tenantId } = useTenant();
  const invoiceTrackingLink = useInvoiceTrackingLink();
  const [params] = useSearchParams();
  // The list's row link carries the reader's filters here, so going back keeps them.
  const listLink = `/o/${tenantId}/waste/invoices${params.size > 0 ? `?${params}` : ""}`;
  const showManifestViewsV2 = useManifestViewsV2();
  const {
    selectedId: processedManifestId,
    pushIdToUrl: openManifest,
    popIdFromUrl: closeManifest,
  } = useSelectedIdInUrl("processedManifestId");
  const { hasPermissions } = useAuthorization();
  const alerts = useAlerts();
  const canDownloadInvoiceDocument = hasPermissions([
    Permission.ReadAllFacility,
    Permission.WriteAllFacility,
  ]);
  const { data, loading, error } = useQuery(WASTE_INVOICE_DETAIL_QUERY, {
    variables: { id: wasteInvoiceId ?? "" },
    skip: !wasteInvoiceId,
    fetchPolicy: "cache-and-network",
  });
  const [getDownloadUrl, { loading: downloadUrlLoading }] = useLazyQuery(
    GET_WASTE_INVOICE_DOCUMENT_DOWNLOAD_URL,
    { fetchPolicy: "network-only" }
  );
  const invoice = data?.wasteInvoice;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Invoice Tracking", to: tenantId ? invoiceTrackingLink("") : undefined },
    ...(invoice ? [{ label: `Invoice ${invoice.invoiceNumber}` }] : []),
  ]);

  const downloadInvoiceDocument = async () => {
    if (!wasteInvoiceId || !invoice?.sourceDocument) return;
    try {
      const result = await getDownloadUrl({ variables: { id: wasteInvoiceId } });
      const downloadUrl = result.data?.getWasteInvoiceDocumentDownloadUrl;
      if (!downloadUrl) {
        alerts.error("Unable to download invoice document");
        return;
      }
      window.open(downloadUrl, "_blank", "noopener");
    } catch (downloadError) {
      alerts.error(
        "Unable to download invoice document",
        downloadError instanceof Error ? downloadError : undefined
      );
    }
  };

  return (
    <>
      <InvoiceTrackingDetailPage
        backLink={listLink}
        backLabel="All invoices"
        actions={
          invoice && (
            <>
              <Button component={Link} to={invoiceTrackingLink(`/audit/${invoice.id}`)}>
                View audit
              </Button>
              {canDownloadInvoiceDocument && invoice.sourceDocument != null && (
                <Button
                  startIcon={<Download />}
                  loading={downloadUrlLoading}
                  onClick={downloadInvoiceDocument}
                >
                  Download invoice document
                </Button>
              )}
            </>
          )
        }
      >
        <InvoiceDetailBody
          invoice={invoice}
          loading={loading}
          error={error}
          tenantId={tenantId}
          onManifestClick={openManifest}
        />
      </InvoiceTrackingDetailPage>
      {showManifestViewsV2 ? (
        <ManifestDetailsV2
          processedManifestId={processedManifestId}
          open={!!processedManifestId}
          onClose={closeManifest}
        />
      ) : (
        <ManifestDetailsV1
          processedManifestId={processedManifestId}
          open={!!processedManifestId}
          onClose={closeManifest}
        />
      )}
    </>
  );
}
